import { Geolocation } from '@capacitor/geolocation'
import { IframeBridgeService, isMiseIframe } from './IframeBridgeService'

export const GeolocationService = {
	watchCallbacks: new Map(),
	iframeWatchId: 0,
	iframeWatchIntervals: new Map(),

	async getCurrentPosition(options = {}) {
		// In modalità iframe, usa il bridge per richiedere la posizione all'app parent
		if (isMiseIframe()) {
			try {
				const position = await IframeBridgeService.requestLocation({
					enableHighAccuracy: options.enableHighAccuracy !== false,
					timeout: options.timeout || 30000,
					maximumAge: options.maximumAge || 0
				})
				return position
			} catch (error) {
				// Trasforma l'errore nel formato GeolocationPositionError
				const geoError = new Error(error.message || 'Geolocation error')
				geoError.code = error.code || 2
				throw geoError
			}
		}

		// Altrimenti usa Capacitor Geolocation
		const position = await Geolocation.getCurrentPosition({
			enableHighAccuracy: options.enableHighAccuracy !== false,
			timeout: options.timeout || 30000,
			maximumAge: options.maximumAge || 0
		})
		return position
	},

	async watchPosition(successCallback, errorCallback, options = {}) {
		// In modalità iframe, simula il watch con polling
		if (isMiseIframe()) {
			const watchId = `iframe_watch_${++this.iframeWatchId}`
			const interval = options.timeout || 10000

			const pollPosition = async () => {
				try {
					const position = await IframeBridgeService.requestLocation({
						enableHighAccuracy: options.enableHighAccuracy !== false,
						timeout: interval,
						maximumAge: 0
					})
					successCallback(position)
				} catch (error) {
					const geoError = new Error(error.message || 'Geolocation error')
					geoError.code = error.code || 2
					errorCallback(geoError)
				}
			}

			// Prima chiamata immediata
			pollPosition()

			// Poi polling periodico
			const intervalId = setInterval(pollPosition, interval)
			this.iframeWatchIntervals.set(watchId, intervalId)

			return watchId
		}

		// Altrimenti usa Capacitor Geolocation
		const watchId = await Geolocation.watchPosition({
			enableHighAccuracy: options.enableHighAccuracy !== false,
			timeout: options.timeout || 10000
		}, (position, err) => {
			if (err) errorCallback(err)
			else if (position) successCallback(position)
		})
		return watchId
	},

	async clearWatch(watchId) {
		// In modalità iframe, pulisci l'intervallo
		if (typeof watchId === 'string' && watchId.startsWith('iframe_watch_')) {
			const intervalId = this.iframeWatchIntervals.get(watchId)
			if (intervalId) {
				clearInterval(intervalId)
				this.iframeWatchIntervals.delete(watchId)
			}
			return
		}

		// Altrimenti usa Capacitor
		await Geolocation.clearWatch({ id: watchId })
	},

	async checkPermissions() {
		// In modalità iframe, assumiamo che i permessi siano gestiti dall'app parent
		if (isMiseIframe()) {
			return { location: 'granted' }
		}
		return await Geolocation.checkPermissions()
	},

	async requestPermissions() {
		// In modalità iframe, i permessi sono gestiti dall'app parent
		if (isMiseIframe()) {
			return { location: 'granted' }
		}
		return await Geolocation.requestPermissions()
	}
}
